/**
 * État de la journée courante et mutations du journal
 * (ARCHITECTURE.md §6.1 et §6.2, specs §6.2).
 *
 * ⚠️ **Mise à jour optimiste du bandeau de restant** (§6.2). C'est le point
 * central de ce fichier, et la raison pour laquelle la cible des 15 secondes
 * (specs §4) est tenable : à la validation d'une entrée, le bandeau bouge tout
 * de suite, avant la réponse du serveur. L'aller-retour reste bien sûr
 * nécessaire, mais l'utilisateur n'a pas à l'attendre pour voir son restant.
 *
 * ⚠️ Le calcul local est une **prévisualisation**, jamais une source de vérité
 * (§5.1). Il est écrasé par la réponse du serveur dès qu'elle arrive. C'est
 * exactement le partage prévu par l'architecture : « le serveur recalcule
 * toujours, le client ne fait qu'afficher une prévisualisation ».
 *
 * 🔴 **Le retour arrière est scindé depuis la V2** (`ARCHITECTURE_V2.md` §3.5).
 * Jusqu'ici *tout* échec annulait l'aperçu. Mais les deux natures d'échec que
 * distingue désormais `api/client.ts` n'appellent pas la même réponse :
 *
 *   - une erreur **applicative** (le serveur a répondu, et il refuse) annule
 *     l'aperçu et s'affiche — rejouer produirait le même refus ;
 *   - un échec de **transport** (le serveur n'a rien répondu) ne l'annule
 *     plus. La saisie était bonne ; seul le réseau a manqué.
 *
 * Les confondre ferait disparaître de l'écran une entrée pourtant correcte —
 * « le pire des deux mondes » (§3.5), et le défaut même que le mode hors ligne
 * existe pour empêcher.
 *
 * ⚠️ **Comportement transitoire, à remplacer à l'étape 3.** La file de
 * synchronisation n'existe pas encore (§8-3) : rien ne réémettra la requête. En
 * attendant, l'aperçu **reste affiché** et le hook expose `pendingSync`, que
 * l'écran présente en note **neutre** (`PendingNote`) — pas en erreur, le hors
 * ligne étant un mode nominal en V2. La saisie reste donc visible et annoncée
 * comme non confirmée, au lieu de s'évanouir. Elle n'est pas pour autant
 * enregistrée : c'est précisément ce que l'étape 3 viendra tenir, en branchant
 * la file là où `pendingSync` est aujourd'hui calculé.
 *
 * ⚠️ **Une seule clé de cache par journée, sa date.** La route `/` interrogeait
 * `/api/days/today` sans connaître la date à l'avance, ce qui obligeait à tenir
 * deux entrées de cache cohérentes pour une même journée. Elle interroge
 * désormais une date connue — celle du jour **dans le fuseau de l'appareil** —
 * et la seconde clé a disparu avec le risque de divergence qu'elle portait.
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type {
  AdjustEntryIngredientInput,
  DayDTO,
  KcalGap,
  LogEntryDTO,
  LogEntryInput,
  MacroSet,
  MealDTO,
} from '../../../shared/types';
import {
  adjustLogEntryIngredients,
  applyTemplate,
  copyMeal,
  createLogEntry,
  createMeal,
  deleteLogEntry,
  deleteMeal,
  fetchDay,
  updateLogEntry,
  updateMeal,
} from '../api/days';
import { isQueued, isTransportFailure } from '../api/client';
import { newClientId } from '../lib/dates';
import { provisionalIdFor } from '../lib/syncQueue';
import { useConfirm } from './useConfirm';

/**
 * Date `YYYY-MM-DD` d'une journée.
 *
 * ⚠️ **Toujours une date, jamais `'today'`.** Ce sélecteur pouvait valoir la
 * chaîne `'today'` jusqu'à ce que la route `/` interroge `/api/days/today` sans
 * connaître la date à l'avance. Elle la connaît désormais : c'est
 * `browserToday()`, la date du jour **dans le fuseau de l'appareil** — demandé
 * après usage. Le serveur garde sa propre notion du jour (`TZ`, §9), qui pilote
 * les notifications ; ce n'est plus elle qui décide de ce qu'affiche l'écran.
 *
 * Conséquence heureuse : une seule clé de cache par journée. Les deux entrées à
 * tenir cohérentes — la date et `today` — ont disparu, et avec elles le risque
 * qu'une mutation en mette une à jour et pas l'autre.
 */
export type DaySelector = string;

function dayKey(selector: DaySelector) {
  return ['day', selector] as const;
}

/**
 * Options de lecture d'une journée **à une date connue**, partagées par le
 * panneau voisin du pager et par son préchargement.
 *
 * ⚠️ Même clé de cache que `useDay` : une journée découverte pendant un
 * glissement est déjà là quand on atterrit dessus, et l'atterrissage se fait
 * donc sans clignotement.
 *
 * ⚠️ **Lire une journée la crée** (§4.5). C'est pourquoi le pager ne précharge
 * qu'à partir du moment où le doigt a franchi `PEEK_PX` : commencer un
 * défilement, ou tapoter, ne matérialise aucune journée. Précharger les deux
 * voisines dès l'affichage — le réflexe pour un carrousel — figerait « demain »
 * chaque jour, et une surcharge de planning posée le soir pour le lendemain
 * n'aurait alors plus d'effet.
 */
export function dayQueryOptions(date: string) {
  return { queryKey: dayKey(date), queryFn: () => fetchDay(date), staleTime: 60_000 } as const;
}

/* ==========================================================================
 * Arithmétique locale — prévisualisation uniquement (§5.1)
 * ======================================================================= */

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function addMacros(a: MacroSet, b: MacroSet): MacroSet {
  return {
    protein: round2(a.protein + b.protein),
    carbs: round2(a.carbs + b.carbs),
    fat: round2(a.fat + b.fat),
    kcal: round2(a.kcal + b.kcal),
  };
}

function subMacros(a: MacroSet, b: MacroSet): MacroSet {
  return {
    protein: round2(a.protein - b.protein),
    carbs: round2(a.carbs - b.carbs),
    fat: round2(a.fat - b.fat),
    kcal: round2(a.kcal - b.kcal),
  };
}

/** Mêmes bornes que `normalizeMacros` côté serveur, pour que l'aperçu ne mente pas. */
function previewMacros(macros: MacroSet): MacroSet {
  return {
    protein: round2(Math.max(0, macros.protein)),
    carbs: round2(Math.max(0, macros.carbs)),
    fat: round2(Math.max(0, macros.fat)),
    kcal: round2(Math.max(0, macros.kcal)),
  };
}

const NO_MACROS: MacroSet = { protein: 0, carbs: 0, fat: 0, kcal: 0 };

/**
 * L'écart 4/4/9 des cibles d'une journée (§5.3) — **indicatif, jamais imposé**.
 *
 * Jumeau de `kcalGap()` / `theoreticalKcal()` de `services/macros.ts`, aux
 * mêmes constantes et aux mêmes arrondis. Il n'existait pas côté client tant
 * que la cible du jour ne bougeait pas sous les doigts.
 */
function kcalGapOf(targets: MacroSet): KcalGap {
  const theoretical = round2(targets.protein * 4 + targets.carbs * 4 + targets.fat * 9);
  return {
    entered: round2(targets.kcal),
    theoretical,
    delta: round2(targets.kcal - theoretical),
  };
}

/** Recompose les agrégats d'une journée après une modification locale. */
function recomputeDay(day: DayDTO, meals: MealDTO[]): DayDTO {
  const withTotals = meals.map((meal) => {
    const consumed = meal.entries.reduce<MacroSet>(
      (total, entry) => addMacros(total, entry.macros),
      NO_MACROS,
    );
    return { ...meal, consumed, remaining: subMacros(meal.targets, consumed) };
  });

  const consumed = withTotals.reduce<MacroSet>(
    (total, meal) => addMacros(total, meal.consumed),
    NO_MACROS,
  );

  /*
   * 🔴 **La cible du jour est la SOMME de celles des repas**, ici comme dans
   * `serializeDay` — c'est la règle posée le 04/09/2026 avec la migration
   * `0013`. Sans ce calcul local, régler l'objectif d'un repas ne ferait
   * bouger le bandeau qu'au retour du serveur, c'est-à-dire **jamais hors
   * ligne** : l'écran montrerait des jauges de repas qui ne s'additionnent
   * plus à celle du jour.
   *
   * ⚠️ **Doublon assumé, comme `foodPreview.ts` et `recipePreview.ts`** : la
   * même formule vit des deux côtés parce que sans elle il n'y a pas d'aperçu
   * optimiste du tout. En cas de divergence, **le serveur gagne** — `settle`
   * remplace la journée entière par ce qu'il a répondu.
   */
  const targets = withTotals.reduce<MacroSet>(
    (total, meal) => addMacros(total, meal.targets),
    NO_MACROS,
  );

  return {
    ...day,
    meals: withTotals,
    targets,
    consumed,
    remaining: subMacros(targets, consumed),
    /*
     * ⚠️ **L'écart 4/4/9 suit les cibles, sinon il ment le temps de
     * l'aller-retour** — et hors ligne, jusqu'à la prochaine ouverture avec du
     * réseau. Recopié de `services/macros.ts` (`kcalGap`/`theoreticalKcal`),
     * arrondis compris : c'est la même note qui s'affiche.
     */
    kcalGap: kcalGapOf(targets),
  };
}

/* ==========================================================================
 * Cache
 * ======================================================================= */

/**
 * Écrit une journée dans le cache, sous sa date.
 *
 * ⚠️ Une seule clé depuis que la route `/` interroge une date connue : il n'y a
 * plus d'entrée `today` à tenir en parallèle, donc plus de moyen que les deux
 * divergent. Voir `DaySelector`.
 */
function writeDay(queryClient: QueryClient, day: DayDTO): void {
  queryClient.setQueryData(dayKey(day.date), day);
}

/** Contexte de retour arrière : l'entrée de cache avant mutation. */
interface Rollback {
  byDate: [readonly ['day', DaySelector], DayDTO | undefined];
}

/* ==========================================================================
 * Hook
 * ======================================================================= */

/**
 * Ce que le client affiche **avant** la réponse du serveur (§5.1, §6.2).
 *
 * ⚠️ Fourni par l'appelant, jamais déduit ici : l'écran d'ajout sait ce qu'il
 * ajoute — une saisie libre porte ses macros, un aliment les fait calculer par
 * `lib/foodPreview.ts`. Cette prévisualisation **n'est jamais envoyée** au
 * serveur, qui n'en accepterait rien : elle ne sert qu'à faire bouger le bandeau
 * de restant tout de suite, et elle est écrasée par la journée renvoyée.
 */
export interface EntryPreview {
  label: string;
  quantity: number;
  unit: string;
  portionLabel: string | null;
  macros: MacroSet;
  /**
   * Lignes du bloc, pour une entrée de recette (specs §6.5). Omises pour les
   * autres types — un aperçu n'a pas à inventer un bloc vide.
   */
  ingredients?: { label: string; quantity: number; unit: string; macros: MacroSet }[];
}

export interface UseDayResult {
  day: DayDTO | undefined;
  isPending: boolean;
  error: Error | null;
  /** Vrai tant qu'une mutation optimiste n'a pas été confirmée. */
  isSaving: boolean;
  /**
   * Vrai le temps d'une micro-animation, juste après une saisie.
   *
   * Le bandeau de restant s'en sert pour accuser la validation — c'est le
   * **repli visuel de l'haptique**, que Safari iOS ne fournit pas
   * (`lib/haptics.ts`). Il est porté par le bandeau et non par le bouton :
   * le bouton disparaît avec la feuille qu'il referme, le bandeau est
   * précisément ce qu'on regarde ensuite.
   */
  confirming: boolean;
  addEntry: (mealId: number, input: LogEntryInput, preview: EntryPreview) => void;
  editEntry: (entryId: number, input: LogEntryInput, preview: EntryPreview) => void;
  removeEntry: (entryId: number) => void;
  /**
   * Corrige les quantités d'un bloc recette déjà journalisé (specs §6.5-d).
   * Ne touche ni à la recette source, ni aux autres entrées.
   */
  adjustEntry: (entryId: number, lines: AdjustEntryIngredientInput[]) => void;
  /**
   * ⚠️ **`targets` est facultatif.** Omis, les quatre cibles valent zéro, les
   * jauges du repas affichent son total au lieu d'un restant (`MealCard`), et
   * le total de la journée ne bouge pas — il en est la somme.
   */
  addMeal: (name: string, targets?: MacroSet) => void;
  renameMeal: (mealId: number, name: string) => void;
  /**
   * Les objectifs macros propres à un repas (specs §6.2 : chaque repas affiche
   * « son propre sous-total **et son objectif de repas** »).
   *
   * 🔴 **Met aussi à jour la cible de la JOURNÉE**, qui est la somme de celles
   * de ses repas depuis la migration `0013` (04/09/2026). Rien n'est recalculé
   * nulle part : le serveur la calcule à la lecture, `recomputeDay` la
   * recompose localement pour que le bandeau bouge au geste — y compris hors
   * ligne, où la réponse n'arrivera qu'à la prochaine ouverture avec du réseau.
   */
  setMealTargets: (mealId: number, targets: MacroSet) => void;
  removeMeal: (mealId: number) => void;
  /** Marque un repas comme réutilisable depuis l'écran d'ajout. */
  setMealFavorite: (mealId: number, isFavorite: boolean) => void;
  /** Recopie les entrées d'un repas déjà composé dans celui-ci. */
  copyMealHere: (mealId: number, sourceMealId: number) => void;
  applyTemplateHere: (templateId: number) => void;
  /**
   * Une saisie au moins n'a pas été confirmée **faute de réseau** — pas parce
   * qu'elle était fautive (§3.5).
   *
   * 🔴 **À présenter en note neutre, jamais en erreur.** En V2 le hors ligne
   * est un mode nominal : `mutationError` et `pendingSync` sont donc deux
   * champs distincts, et non deux valeurs d'un même champ, précisément pour
   * qu'on ne puisse pas les afficher de la même façon par distraction.
   *
   * ⚠️ **Booléen, et non un compteur — transitoire.** Compter les opérations en
   * attente demande de les *tenir*, ce qui est le travail de la file (§3.3,
   * étape 3). Ici on ne sait qu'une chose, mais on la sait sûrement : quelque
   * chose n'est pas parti. Le compteur (« 12 en attente ») viendra avec la file.
   */
  pendingSync: boolean;
  /**
   * Erreur **applicative** à afficher — jamais un échec de transport.
   *
   * ⚠️ Sans ce filtre, couper le réseau ferait apparaître un bandeau rouge sur
   * une journée qui se remplit parfaitement : « ne jamais présenter hors ligne
   * comme une erreur » (§3.5).
   */
  mutationError: Error | null;
  /**
   * Efface l'erreur de mutation affichée. À appeler quand l'utilisateur quitte
   * le contexte où elle avait un sens — changer de jour, par exemple : sans
   * cela, TanStack Query conserve l'erreur jusqu'à la mutation suivante et le
   * bandeau rouge suit l'utilisateur d'une journée à l'autre. Constaté sur
   * capture d'écran, pas au typecheck.
   */
  dismissError: () => void;
}

/**
 * @param selector date `YYYY-MM-DD` de la journée, y compris pour la route `/`
 *   où elle vaut la date du jour **dans le fuseau de l'appareil**.
 */
export function useDay(selector: DaySelector): UseDayResult {
  const queryClient = useQueryClient();
  /*
   * Retour de validation (specs §8.1).
   *
   * 🔴 **Déclenché au geste, pas à la réponse du serveur.** Une entrée est
   * écrite en optimiste : le bandeau de restant est à jour en quelques
   * millisecondes (mesuré à l'étape 5), et c'est cette mise à jour-là que la
   * confirmation accompagne. L'attacher à `onSuccess` la ferait arriver un
   * aller-retour plus tard, alors que l'écran a déjà changé — donc au moment
   * où elle n'apprend plus rien.
   *
   * ⚠️ Posé **ici** et non dans chaque écran d'ajout : les quatre voies du
   * §6.3 passent toutes par `addEntry`, et la copie d'un repas par
   * `copyMealHere`. Un seul endroit, donc aucune voie oubliée.
   */
  const { confirming, confirm } = useConfirm();

  const query = useQuery({
    queryKey: dayKey(selector),
    queryFn: () => fetchDay(selector),
    // Le journal ne change que par nos propres mutations, qui réécrivent le
    // cache elles-mêmes : rien à revalider entre deux.
    staleTime: 60_000,
  });

  const day = query.data;

  /** Prépare un retour arrière et applique l'aperçu, s'il y en a un. */
  const beginOptimistic = useCallback(
    async (mutate?: (current: DayDTO) => DayDTO): Promise<Rollback> => {
      await queryClient.cancelQueries({ queryKey: ['day'] });

      const current = queryClient.getQueryData<DayDTO>(dayKey(selector));
      const rollback: Rollback = {
        byDate: [
          dayKey(current?.date ?? selector),
          queryClient.getQueryData<DayDTO>(dayKey(current?.date ?? selector)),
        ],
      };

      if (current && mutate) writeDay(queryClient, mutate(current));
      return rollback;
    },
    [queryClient, selector],
  );

  const rollbackTo = useCallback(
    (context: Rollback | undefined) => {
      if (!context) return;
      const [key, value] = context.byDate;
      if (value) queryClient.setQueryData(key, value);
    },
    [queryClient],
  );

  /**
   * Réponse à l'échec d'une mutation — le point exact où la V2 scinde le retour
   * arrière (§3.5).
   *
   * 🔴 **Un échec de transport ne défait rien.** La saisie était bonne, seul le
   * réseau a manqué : effacer l'aperçu punirait l'utilisateur d'une panne qui
   * ne le concerne pas, et lui ferait ressaisir ce qui n'avait rien de faux.
   * Une erreur **applicative**, elle, annule comme avant — le serveur a répondu
   * et il refuse, rejouer produirait le même refus.
   *
   * ⚠️ Le critère est « **le serveur a-t-il répondu ?** », pas la classe de
   * statut : un 500 reste annulé et affiché, exactement comme avant cette
   * étape. Voir `isTransportFailure` dans `api/client.ts`, où le choix du seuil
   * est argumenté.
   */
  const onMutationError = useCallback(
    // ⚠️ `error: Error`, et non `unknown` : TanStack Query **infère** `TError`
    // depuis ce gestionnaire. Le typer `unknown` faisait retomber `.error` en
    // `unknown` sur les quatre mutations, et `mutationError` cessait d'être une
    // `Error`. Trouvé au typecheck, invisible à la lecture.
    (error: Error, _variables: unknown, context: Rollback | undefined) => {
      if (isTransportFailure(error)) return;
      rollbackTo(context);
    },
    [rollbackTo],
  );

  const settle = useCallback(
    (fresh: DayDTO) => {
      writeDay(queryClient, fresh);
      /*
       * ⚠️ L'accès rapide dépend du journal : « récents » est trié par date de
       * la **dernière entrée** qui référence chaque aliment (specs §6.3-a). Sans
       * cette invalidation, l'aliment qu'on vient d'ajouter n'apparaîtrait en
       * tête qu'au rechargement suivant de l'application — c'est-à-dire jamais,
       * pour une PWA qu'on ne ferme pas.
       *
       * Invalidation et non réécriture : la liste est petite, elle se recharge
       * en une requête, et elle n'est visible qu'à l'ouverture de l'écran
       * d'ajout — donc jamais pendant que l'utilisateur la regarde.
       */
      void queryClient.invalidateQueries({ queryKey: ['quick-access'] });
    },
    [queryClient],
  );

  /* --- Ajout d'une entrée : le chemin critique -------------------------- */

  const addEntryMutation = useMutation({
    mutationFn: ({
      mealId,
      input,
      clientId,
    }: {
      mealId: number;
      input: LogEntryInput;
      preview: EntryPreview;
      clientId: string;
    }) => createLogEntry(mealId, { ...input, clientId }),

    onMutate: ({ mealId, input, preview, clientId }) =>
      beginOptimistic((current) => {
        /*
         * Entrée provisoire : `id` négatif pour ne jamais entrer en collision
         * avec un identifiant réel. Elle disparaît dès la réponse du serveur.
         *
         * 🔴 **L'identifiant vient de la file depuis la V2**, et non plus d'un
         * `-Date.now()`. Ce n'est pas cosmétique : hors ligne, cette entrée
         * n'existe encore *que* sous ce numéro, et corriger ou supprimer une
         * saisie pas encore partie est un geste normal. La file sait traduire un
         * identifiant qu'elle a attribué en place-titre `{{op:seq}}` ; elle ne
         * saurait rien faire d'un horodatage. Avec l'ancien numéro, un
         * `DELETE /api/entries/-1787…` serait parti tel quel au drainage et
         * aurait répondu 404.
         */
        const ghost: LogEntryDTO = {
          id: provisionalIdFor(clientId),
          clientId,
          type: input.type,
          // L'aperçu ne référence rien : les deux identifiants informatifs
          // arrivent avec la réponse du serveur, seul juge de ce qu'il a écrit.
          foodId: null,
          recipeId: null,
          label: preview.label,
          quantity: preview.quantity,
          unit: preview.unit,
          portionLabel: preview.portionLabel,
          macros: previewMacros(preview.macros),
          // Aperçu du bloc groupé, pour qu'une recette apparaisse repliable dès
          // la validation. Les `id` sont provisoires et négatifs, comme celui de
          // l'entrée : le serveur renverra les vrais.
          ingredients: (preview.ingredients ?? []).map((line, index) => ({
            id: -(index + 1),
            foodId: null,
            label: line.label,
            quantity: line.quantity,
            unit: line.unit,
            macros: previewMacros(line.macros),
            position: index,
          })),
          position: Number.MAX_SAFE_INTEGER,
          createdAt: Date.now(),
        };
        return recomputeDay(
          current,
          current.meals.map((meal) =>
            meal.id === mealId ? { ...meal, entries: [...meal.entries, ghost] } : meal,
          ),
        );
      }),

    onError: onMutationError,
    onSuccess: settle,
  });

  /* --- Modification et suppression -------------------------------------- */

  const editEntryMutation = useMutation({
    mutationFn: ({
      entryId,
      input,
    }: {
      entryId: number;
      input: LogEntryInput;
      preview: EntryPreview;
    }) => updateLogEntry(entryId, input),

    onMutate: ({ entryId, preview }) =>
      beginOptimistic((current) =>
        recomputeDay(
          current,
          current.meals.map((meal) => ({
            ...meal,
            entries: meal.entries.map((entry) =>
              entry.id === entryId
                ? {
                    ...entry,
                    label: preview.label,
                    quantity: preview.quantity,
                    unit: preview.unit,
                    portionLabel: preview.portionLabel,
                    macros: previewMacros(preview.macros),
                  }
                : entry,
            ),
          })),
        ),
      ),

    onError: onMutationError,
    onSuccess: settle,
  });

  const removeEntryMutation = useMutation({
    mutationFn: (entryId: number) => deleteLogEntry(entryId),

    onMutate: (entryId) =>
      beginOptimistic((current) =>
        recomputeDay(
          current,
          current.meals.map((meal) => ({
            ...meal,
            entries: meal.entries.filter((entry) => entry.id !== entryId),
          })),
        ),
      ),

    onError: onMutationError,
    onSuccess: settle,
  });

  /* --- Ajustement d'un bloc recette (specs §6.5-d) ---------------------- */

  /*
   * ⚠️ Aperçu local **proportionnel**, jamais recalculé depuis les aliments : le
   * client ne connaît pas `food.*_100` pour les lignes d'un bloc déjà écrit, et
   * il n'a pas à le connaître (§5.1). La règle de trois sur les macros figées
   * donne exactement le même résultat que le serveur tant que la fiche n'a pas
   * changé — et quand elle a changé, c'est la réponse du serveur qui gagne, le
   * temps d'un aller-retour.
   */
  const adjustEntryMutation = useMutation({
    mutationFn: ({
      entryId,
      lines,
    }: {
      entryId: number;
      lines: AdjustEntryIngredientInput[];
    }) => adjustLogEntryIngredients(entryId, { ingredients: lines }),

    onMutate: ({ entryId, lines }) =>
      beginOptimistic((current) => {
        const wanted = new Map(lines.map((line) => [line.id, Math.max(0, line.quantity)]));
        return recomputeDay(
          current,
          current.meals.map((meal) => ({
            ...meal,
            entries: meal.entries.map((entry) => {
              if (entry.id !== entryId) return entry;

              const kept = entry.ingredients.flatMap((line) => {
                const quantity = wanted.get(line.id) ?? line.quantity;
                if (quantity <= 0) return [];
                const factor = line.quantity > 0 ? quantity / line.quantity : 0;
                return [
                  {
                    ...line,
                    quantity: round2(quantity),
                    macros: previewMacros({
                      protein: line.macros.protein * factor,
                      carbs: line.macros.carbs * factor,
                      fat: line.macros.fat * factor,
                      kcal: line.macros.kcal * factor,
                    }),
                  },
                ];
              });

              return {
                ...entry,
                ingredients: kept,
                macros: kept.reduce<MacroSet>(
                  (total, line) => addMacros(total, line.macros),
                  NO_MACROS,
                ),
              };
            }),
          })),
        );
      }),

    onError: onMutationError,
    onSuccess: settle,
  });

  /* --- Repas : moins critiques, pas d'aperçu local ----------------------- */

  /**
   * Ajout d'un repas.
   *
   * 🔴 **Optimiste depuis la V2, et c'est ce qui rend la composition hors ligne
   * possible** (§2.3-a, étape 3). Sans aperçu local, un repas créé sans réseau
   * n'apparaissait **pas du tout** : il n'y avait donc aucun bouton « + » sur
   * lequel appuyer pour y loguer quoi que ce soit, et le critère de l'étape —
   * « trois repas en mode avion, dont un dans un repas lui-même créé hors
   * ligne » — était littéralement inatteignable.
   *
   * ⚠️ L'`id` provisoire vient de la file, comme celui d'une entrée : c'est lui
   * que le bouton « + » de ce repas transmettra, et que la file traduira en
   * `{{op:seq}}` au moment de mettre l'entrée en file.
   */
  const addMealMutation = useMutation({
    mutationFn: ({
      name,
      targets,
      clientId,
    }: {
      name: string;
      targets: MacroSet;
      clientId: string;
    }) => createMeal(day?.date ?? selector, { name, targets, clientId }),

    onMutate: ({ name, targets, clientId }) =>
      beginOptimistic((current) =>
        recomputeDay(current, [
          ...current.meals,
          {
            id: provisionalIdFor(clientId),
            clientId,
            name,
            // Le serveur place le repas en dernier (`nextMealPosition`) ; on
            // fait pareil, sans quoi il sauterait de place à la réconciliation.
            position: Number.MAX_SAFE_INTEGER,
            /*
             * ⚠️ **Les cibles saisies, ou zéro — jamais une part du total**,
             * qui serait une invention. Un repas ajouté à la main n'avait
             * aucun objectif propre jusqu'au 04/09/2026 ; il peut désormais en
             * recevoir un à la création, et c'est celui-là qu'on montre tout
             * de suite. `recomputeDay` en tire le restant.
             */
            targets,
            consumed: NO_MACROS,
            remaining: NO_MACROS,
            isFavorite: false,
            entries: [],
          },
        ]),
      ),

    onError: onMutationError,
    onSuccess: settle,
  });

  const renameMealMutation = useMutation({
    mutationFn: ({ mealId, name }: { mealId: number; name: string }) =>
      updateMeal(mealId, { name }),

    onMutate: ({ mealId, name }) =>
      beginOptimistic((current) =>
        recomputeDay(
          current,
          current.meals.map((meal) => (meal.id === mealId ? { ...meal, name } : meal)),
        ),
      ),

    onError: onMutationError,
    onSuccess: settle,
  });

  /**
   * Les objectifs macros d'un repas — le jumeau exact du renommage, sur
   * l'autre champ que `PATCH /api/meals/:id` accepte.
   *
   * ⚠️ **Aucune route ni aucun champ nouveaux** : `UpdateMealBody.targets`
   * existe depuis l'étape 5, il n'était simplement écrit que par le modèle
   * appliqué à la création. C'est l'écran qui manquait, pas l'API.
   *
   * ⚠️ **Aperçu optimiste, comme le renommage.** `recomputeDay` recalcule le
   * restant du repas à partir des cibles qu'on vient de poser : la jauge bouge
   * au moment du geste, y compris hors ligne où la réponse n'arrivera qu'à la
   * prochaine ouverture avec du réseau.
   */
  const mealTargetsMutation = useMutation({
    mutationFn: ({ mealId, targets }: { mealId: number; targets: MacroSet }) =>
      updateMeal(mealId, { targets }),

    onMutate: ({ mealId, targets }) =>
      beginOptimistic((current) =>
        recomputeDay(
          current,
          current.meals.map((meal) => (meal.id === mealId ? { ...meal, targets } : meal)),
        ),
      ),

    onError: onMutationError,
    onSuccess: settle,
  });

  const removeMealMutation = useMutation({
    mutationFn: (mealId: number) => deleteMeal(mealId),

    onMutate: (mealId) =>
      beginOptimistic((current) =>
        recomputeDay(
          current,
          current.meals.filter((meal) => meal.id !== mealId),
        ),
      ),

    onError: onMutationError,
    onSuccess: settle,
  });

  /**
   * Marque un repas comme réutilisable depuis l'écran d'ajout.
   *
   * ⚠️ Invalide **aussi** l'accès rapide : les listes de repas y vivent, et sans
   * cela l'étoile qu'on vient de poser n'apparaîtrait qu'au rechargement suivant.
   */
  const favoriteMealMutation = useMutation({
    mutationFn: ({ mealId, isFavorite }: { mealId: number; isFavorite: boolean }) =>
      updateMeal(mealId, { isFavorite }),

    onMutate: ({ mealId, isFavorite }) =>
      beginOptimistic((current) =>
        recomputeDay(
          current,
          current.meals.map((meal) => (meal.id === mealId ? { ...meal, isFavorite } : meal)),
        ),
      ),

    onError: onMutationError,
    onSuccess: settle,
  });

  /**
   * Recopie un repas déjà composé dans celui-ci.
   *
   * ⚠️ **Aucune mise à jour optimiste ici**, contrairement à l'ajout d'une
   * entrée. Ce n'est pas un oubli : une copie ajoute *n* entrées d'un coup, donc
   * prévoir l'état local demanderait de reconstruire côté client des lignes que
   * seul le serveur sait composer — exactement ce que le §5.1 refuse. Le geste
   * n'est pas non plus sur le chemin des 15 secondes : il *remplace* une dizaine
   * de saisies, un aller-retour de plus y est invisible.
   */
  const copyMealMutation = useMutation({
    mutationFn: ({ mealId, sourceMealId }: { mealId: number; sourceMealId: number }) =>
      copyMeal(mealId, { sourceMealId, clientId: newClientId() }),
    onSuccess: settle,
  });

  const applyTemplateMutation = useMutation({
    mutationFn: (templateId: number) =>
      applyTemplate(day?.date ?? selector, { templateId }),
    onSuccess: settle,
  });

  const mutations = [
    addEntryMutation,
    editEntryMutation,
    removeEntryMutation,
    adjustEntryMutation,
    addMealMutation,
    renameMealMutation,
    mealTargetsMutation,
    removeMealMutation,
    favoriteMealMutation,
    copyMealMutation,
    applyTemplateMutation,
  ];

  return {
    day,
    isPending: query.isPending,
    error: query.error,
    isSaving: mutations.some((mutation) => mutation.isPending),
    confirming,
    /*
     * 🔴 **`isQueued` et non `isTransportFailure` depuis l'étape 3.** Les deux
     * seraient vrais ici — toutes les routes du journal sont dans la liste
     * blanche, donc un échec de transport y devient une mise en file — mais ils
     * ne disent pas la même chose, et c'est le message affiché qui en dépend :
     * « c'est en file, ça partira tout seul » n'est vrai que du premier. Si une
     * route du journal sortait un jour de la liste blanche, ce prédicat-ci
     * cesserait de mentir de lui-même.
     */
    pendingSync: mutations.some((mutation) => isQueued(mutation.error)),
    mutationError:
      mutations.find((mutation) => mutation.error && !isTransportFailure(mutation.error))?.error ??
      null,

    dismissError: () => {
      for (const mutation of mutations) {
        if (mutation.error) mutation.reset();
      }
    },

    addEntry: (mealId, input, preview) => {
      // Identifiant tiré ici, une fois : un rejeu — file hors ligne V2 (§11),
      // ou simple double tap — réutilise le même et ne crée pas de doublon.
      addEntryMutation.mutate({ mealId, input, preview, clientId: newClientId() });
      confirm();
    },
    editEntry: (entryId, input, preview) => {
      editEntryMutation.mutate({ entryId, input, preview });
    },
    removeEntry: (entryId) => {
      removeEntryMutation.mutate(entryId);
    },
    adjustEntry: (entryId, lines) => {
      adjustEntryMutation.mutate({ entryId, lines });
    },
    addMeal: (name, targets) => {
      // Identifiant tiré ici, une fois, comme pour une entrée : un rejeu
      // réutilise le même et ne crée pas un second repas.
      addMealMutation.mutate({ name, targets: targets ?? NO_MACROS, clientId: newClientId() });
    },
    renameMeal: (mealId, name) => {
      renameMealMutation.mutate({ mealId, name });
    },
    setMealTargets: (mealId, targets) => {
      mealTargetsMutation.mutate({ mealId, targets });
    },
    setMealFavorite: (mealId, isFavorite) => {
      favoriteMealMutation.mutate({ mealId, isFavorite });
    },
    copyMealHere: (mealId, sourceMealId) => {
      copyMealMutation.mutate({ mealId, sourceMealId });
      confirm();
    },
    removeMeal: (mealId) => {
      removeMealMutation.mutate(mealId);
    },
    applyTemplateHere: (templateId) => {
      applyTemplateMutation.mutate(templateId);
    },
  };
}
